import React, { useEffect, useRef, useState } from 'react';
import { Button, useTheme } from '@mui/material';
import ListSubheader from '@mui/material/ListSubheader';
import List from '@mui/material/List';
import Collapse from '@mui/material/Collapse';
import { useOfflineDetectContext } from "../../../contexts/offline-detect-context/UseOfflineDetectContext";
import { INavigationItem } from '../../../services/navigation-data-service/Models';
import NavigationDataService from '../../../services/navigation-data-service/NavigationDataService';
import { MobileGroupListItem } from './MobileGroupListItem';
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { useUserContext } from '../../../contexts/user-context/UserContext';

export type MobileGroupHierarchyProps = {
    onSelected: (selectedItem: INavigationItem) => void;
    onLoading?: (isLoading: boolean) => void;    

    rootID?: number;
};

export default function MobileGroupHierarchy({onSelected, 
    onLoading,
    rootID
}: MobileGroupHierarchyProps) {

    const isMountedRef = useRef<boolean>(undefined);
    const theme = useTheme();
    const [open, setOpen] = useState(true);
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const [treeData, setTreeData] = useState<INavigationItem>();
    const routeContext = useRouteContext();
    const [expandedItem, setExpandedItem] = useState<INavigationItem>();

    const [contextGroupID] = useState(routeContext.groupID);

    useEffect(() => {
        isMountedRef.current = true;

        const getTreeData =  async (customerID: number, groupID: number) => {
            
            const data = await NavigationDataService.getNavigationGroup(
                customerID,
                groupID, {
                includeChildren: true,
                includeAncestors: rootID === undefined ? true : false
            });
    
            if (data && isMountedRef.current) {
                setTreeData(data);

                if (onLoading) {
                    onLoading(false);
                }
            }
            
        };

        if (((userState.customer && contextGroupID) || offlineState.offlineDataLoaded)) {
            if (onLoading) {
                onLoading(true);
            }

            const groupID = expandedItem?.objectID ?? rootID ?? contextGroupID as number;
            
            getTreeData(userState.customer?.id as number, groupID);
        }

        return () => {
            isMountedRef.current = false;
        }

    }, [userState.customer,
        contextGroupID,
        offlineState.offlineDataLoaded,
        setTreeData,
        expandedItem,
        onLoading,
        rootID]);

    const onBack =  async (item: INavigationItem) => {

        let groupWithAncestors = await NavigationDataService.getNavigationGroup(
            userState.customer?.id as number,
            item.objectID, {
            includeChildren: true,
            includeAncestors: true
        });                

        setExpandedItem(undefined);
        setTreeData(groupWithAncestors);
    };

    const onExpand = async (item: INavigationItem) => {        

        setExpandedItem(item);
        setOpen(true);
    };

    const onToggleItem = async (item: INavigationItem) => {
    
        if (!item.parentID) {
            setOpen(!open);
        } else {
            const parent = item.ancestors?.find(o => o.objectID === item.parentID);
            if (parent) {
                await onBack(parent);
            }
        }
    };

    
    const onSelectItem = async (item: INavigationItem) => {
        
        if (treeData?.objectID !== item.objectID){
            await onExpand(item);
        }

        setExpandedItem(undefined);

        onSelected(item);
    };

    return (
        <List
            sx={{
                marginTop: theme.spacing(1),
                flexGrow: 1,
                width: '100%',
                backgroundColor: theme.palette.background.paper,
                overflowY: 'auto'
            }}
            component="nav"
            aria-labelledby="ancestor-list"
            subheader={
                <ListSubheader component="div" id="ancestor-list" 
                    sx={{
                        display: 'flex',
                        flexDirection:'column'
                    }}
                    >
                    {
                        treeData?.ancestors?.map((item, index) => 
                            <Button key={item.objectID}
                                sx={{
                                    '& + &': {
                                        marginTop: theme.spacing(1)
                                    }
                                }}
                                fullWidth
                                variant="contained"
                                color="secondary"
                                onClick={(e) => { e.persist(); onBack(item)}}>
                                {`${item.title}/`}
                            </Button>
                        )
                    }
                
                </ListSubheader>
        }>

            {/* display the context group */}
            <MobileGroupListItem data={treeData as INavigationItem} 
                        open={open} 
                        onToggle={onToggleItem} 
                        onSelected={onSelectItem} />

            <Collapse  in={open} timeout="auto" unmountOnExit>
                <List 
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                    }}
                    component="div" disablePadding>
                    {
                        treeData?.children?.map((item) => 
                            <MobileGroupListItem key={item.objectID} 
                                sx={{
                                    paddingLeft: theme.spacing(4),                                    
                                }}
                                data={item}
                                open={false}
                                onToggle={onExpand}
                                onSelected={onSelectItem} />
                        )
                    }
                </List>
            </Collapse>            
        </List>
    );
}
